<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { ref, computed, unref, watch, toRef } from 'vue';
import * as yup from 'yup';
import { useForm } from 'vee-validate';
import { storeToRefs } from 'pinia';
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router';

import DynamicTabs from '@/components/DynamicTabs.vue';
import FormActions from '@/components/FormActions.vue';
import ImageField from '@/components/ImageField.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import StandardToggle from '@/components/StandardToggle.vue';
import ContextList from '@/components/ContextList.vue';
import FormList from '@/components/FormList.vue';
import ConsultationCompletedBanner from '@/components/consultationCompletedBanner.vue';

import type { Locales, SectionDto, ConsultationDto } from '@/services/types/api.types';
import type { SavingOptions } from '@/services/types/file-uploader.types';
import { provideEditLocale } from '@/composables/data-display-utils';
import { localizableString } from '@/helpers/validation.helper';
import { useLanguageValidationErrors } from '@/composables';
import { useWatchItemLoading } from '@/composables/watch-item-loading';
import { useSectionsStore, useCommunityStore, useConsultationsStore } from '@/stores';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import {
  dynamicTabsNavigationGuard,
  formSaveImageGuard,
  formSaveNavigationGuard,
  hasRouteChanged,
  type DynamicTabsNavigationGuardCallbacks,
  resetFormToItem,
} from '@/helpers/form.helper';
import { Constants } from '@/constants';

// CONST
const { t } = useI18n();

// REFS
const openedTab = ref<string>('infos');
const consultation = ref<ConsultationDto | undefined>(undefined);
const dynamicTabsRef = ref<InstanceType<typeof DynamicTabs> | null>(null);

// STORE
const sectionsStore = useSectionsStore();
const communityStore = useCommunityStore();
const consultationsStore = useConsultationsStore();
const { community } = storeToRefs(communityStore);

// PROPS
const props = defineProps<{
  languageEnabled: Locales[];
  sectionId: number;
  isTemplate: boolean;
}>();

const sectionId = toRef(props, 'sectionId');

// FORM
const validationSchema = computed(() =>
  yup.object<SectionDto>({
    title: localizableString(props.languageEnabled),
    description: localizableString(props.languageEnabled),
    banner_file: yup
      .object()
      .shape({
        url: yup.string().optional(),
        altText: localizableString(props.languageEnabled, { optional: true }),
      })
      .nullable(),
  }),
);
const formContext = useForm({ validationSchema });
const { handleSubmit, errors, meta, resetForm } = formContext;
const languageErrors = useLanguageValidationErrors(errors);

const section = useWatchItemLoading<SectionDto>(sectionId, sectionsStore, (s) => resetFormToItem(formContext, s));

const editLocale = provideEditLocale(toRef(() => props.languageEnabled));

// COMPUTED
const tabs = computed(() => [
  { id: 'infos', name: t('section.tabs.infos'), isDirty: isDirty.value, icon: 'o_info' },
  { id: 'forms', name: t('section.tabs.forms'), icon: 'o_article' },
  { id: 'contexts', name: t('section.tabs.contexts'), icon: 'link' },
  { id: 'options', name: t('section.tabs.options'), icon: 'more_vert' },
]);

const isDirty = computed(() => {
  return unref(meta).dirty;
});

defineExpose({
  executeNavigationGuard,
});

// LIFECYCLE
watch(
  sectionId,
  async (newSectionId) => {
    consultation.value = undefined;
    if (newSectionId == null) {
      return;
    }
    if (openedTab.value !== 'infos') {
      openedTab.value = 'infos';
      dynamicTabsRef.value?.setActiveTab('infos');
    }
    await sectionsStore.fetchSection(newSectionId);
  },
  { immediate: true },
);

watch(
  section,
  (newSection) => {
    if (!newSection) {
      consultation.value = undefined;
      return;
    }
    consultation.value = consultationsStore.getConsultation(newSection.consultation_id);
  },
  { immediate: true },
);

// ROUTER
onBeforeRouteLeave(formSaveNavigationGuard(meta));
onBeforeRouteUpdate((to, from, next) => {
  if (!hasRouteChanged(to, from)) {
    return formSaveNavigationGuard(meta)(to, from, next);
  }
  next();
});

// FUNCTIONS
const onSubmit = handleSubmit(async (values) => {
  console.debug('submitting', values);
  wrapSavingOperation(
    async () => await sectionsStore.saveSection(values as SectionDto),
    () => resetForm({ values: section.value! }),
  );
});

function onCancel() {
  if (section.value) {
    resetForm({ values: section.value });
  } else {
    console.warn('onCancel: form is null');
  }
}

const handleSaveBanner = async (options: SavingOptions) => {
  await sectionsStore.saveSectionBanner(sectionId.value, options);
};

const removeSectionBanner = () => {
  sectionsStore.clearSectionBanner(sectionId.value);
};

const handleClearBanner = async () => {
  formSaveImageGuard(isDirty.value, removeSectionBanner);
};

function executeNavigationGuard(callbackFns?: DynamicTabsNavigationGuardCallbacks) {
  dynamicTabsNavigationGuard(meta, callbackFns);
}

const handleUpdate = (tabId: string) => {
  openedTab.value = tabId;
};
</script>
<template>
  <div class="section-create-tab" v-if="section">
    <consultation-completed-banner
      v-if="consultationsStore.isCompleted(section.consultation_id) && consultation"
      :status="consultation.status"
    />
    <dynamic-tabs ref="dynamicTabsRef" :tabs="tabs" :active="openedTab" keep-alive @update="handleUpdate">
      <template #infos>
        <form-actions
          :onCancel="onCancel"
          :onSubmit="onSubmit"
          :languageErrors="languageErrors"
          :meta="meta"
          v-model:editLocale="editLocale"
          :language-enabled="languageEnabled"
        />
        <localized-input
          name="title"
          :label="t('section.title')"
          :editLocale="editLocale"
          outlined
          :language_enabled="languageEnabled"
        />
        <localized-input
          name="description"
          :label="t('section.description')"
          :editLocale="editLocale"
          type="textarea"
          outlined
          :language_enabled="languageEnabled"
        />
        <standard-toggle name="contextOpened" :label="t('section.contextOpened')" outlined />
        <standard-toggle name="attachmentsOpened" :label="t('global.attachments-opened')" outlined />
        <image-field
          label="Image bannière"
          :source="section.banner_file?.url"
          :target-width="1920"
          :target-height="360"
          @clear-image="handleClearBanner"
          :on-save-image="handleSaveBanner"
          :form-dirty="isDirty"
          :language-enabled="languageEnabled"
          alt-text-name="banner_file.altText"
        />
      </template>
      <template #contexts>
        <context-list
          :parent="Constants.Context.Parent.Section"
          :parent-id="sectionId"
          :languageEnabled="languageEnabled"
        />
      </template>
      <template #forms>
        <form-list
          :sectionId="sectionId"
          :consultation-id="section.consultation_id"
          :consultation="consultation"
          :languageEnabled="languageEnabled"
          :isTemplate="props.isTemplate"
        />
      </template>
      <template #options>
        <form-actions
          :show-language-switcher="false"
          v-model:editLocale="editLocale"
          :onCancel="onCancel"
          :onSubmit="onSubmit"
          :languageErrors="languageErrors"
          :meta="meta"
          :language-enabled="languageEnabled"
        />
        <q-page>
          <q-card flat class="q-mb-md">
            <q-card-section>
              <standard-toggle
                name="options.citizen_proposal"
                :disabled="!community?.options.consultation.citizen_proposal || !consultation?.options.citizen_proposal"
                :label="t('section.options.citizen_proposal')"
                outlined
              />
              <standard-toggle
                name="options.hide_results_comments"
                :label="t('section.options.hide-results-comments')"
                outlined
              />
            </q-card-section>
          </q-card>
        </q-page>
      </template>
    </dynamic-tabs>
  </div>
</template>
